import * as React from 'react'
import hoistNonReactStatic from 'hoist-non-react-statics'

import { transition } from '../utils/style'
import * as BX24LiveChat from '../utils/bx24livechat'

type ContentRenderer = (props: ModalContentProps) => React.ReactNode

export type ModalContentProps = {
  open: (renderContent: ContentRenderer) => void
  close: () => void
}

type ModalContextValue = {
  isVisible: boolean
  open: (renderContent: ContentRenderer) => void
  close: () => void
}

const defaultContextValue: ModalContextValue = {
  isVisible: false,
  open: () => {},
  close: () => {},
}

export const ModalContext: React.Context<ModalContextValue> =
  React.createContext(defaultContextValue)

type Props = Record<string, any>

type State = ModalContextValue

const defaultContentRenderer: ContentRenderer = () => null

export default function ModalHOCWrapper<T extends Record<string, any>>(
  WrappedComponent: React.ComponentType<T>,
) {
  class ModalHOC extends React.Component<Props & T, State> {
    contentRenderer: ContentRenderer = defaultContentRenderer

    constructor(props: Props & T) {
      super(props)

      this.contentRenderer = defaultContentRenderer

      this.state = {
        isVisible: false,
        open: this.handleOpen,
        close: this.handleClose,
      }
    }

    componentDidMount() {
      document.addEventListener('keydown', this.handleKeyPress, false)
    }

    componentWillUnmount() {
      document.removeEventListener('keydown', this.handleKeyPress, false)
    }

    handleKeyPress = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        this.handleClose()
      }
    }

    handleOverlayPress = () => {
      const { isVisible } = this.state

      if (isVisible) {
        this.handleClose()
      }
    }

    handleOpen = (contentRenderer: ContentRenderer) => {
      this.contentRenderer = contentRenderer

      this.setState(
        {
          isVisible: true,
        },
        () => {
          this.forceUpdate()

          BX24LiveChat.hideButton()
        },
      )
    }

    handleClose = () => {
      this.setState(
        {
          isVisible: false,
        },
        () => {
          this.contentRenderer = defaultContentRenderer

          BX24LiveChat.showButton()
        },
      )
    }

    render() {
      const { isVisible } = this.state
      const ContentRendererComponent = this.contentRenderer

      const wrapperStyle: React.CSSProperties = {
        transition: `filter 0.3s ${transition.timingFunction}`,
        ...(isVisible ? { filter: 'blur(5px)' } : {}),
      }

      const modalStyle: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 100,
        display: 'flex',
        width: '100%',
        minHeight: '100vh',
        pointerEvents: 'none',
      }

      return (
        <React.Fragment>
          <div onClick={this.handleOverlayPress} style={wrapperStyle}>
            <ModalContext.Provider value={this.state}>
              <WrappedComponent {...this.props} />
            </ModalContext.Provider>
          </div>
          {isVisible ? (
            <div style={modalStyle}>
              <ContentRendererComponent
                open={this.handleOpen}
                close={this.handleClose}
              />
            </div>
          ) : null}
        </React.Fragment>
      )
    }
  }

  hoistNonReactStatic(ModalHOC, WrappedComponent)
  return ModalHOC
}
